import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';

const FADE_SIZE = 24;

/**
 * Soft edge-fade for a scrollbar-less scroll container: the top fades in once
 * content is scrolled above, the bottom fades while more content remains below.
 * Sizes are exposed as the `--fade-top` / `--fade-bottom` CSS vars consumed by
 * the `.scroll-fade` mask.
 *
 * Spread `scrollProps` onto the scrollable element and pass a `dependency`
 * (e.g. the item count) that should re-evaluate the fade when it changes.
 */
export const useScrollFade = (dependency?: unknown) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: 0, bottom: 0 });

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setFade({
      top: scrollTop > 1 ? FADE_SIZE : 0,
      bottom: scrollTop + clientHeight < scrollHeight - 1 ? FADE_SIZE : 0,
    });
  }, []);

  useEffect(() => {
    updateFade();
  }, [updateFade, dependency]);

  const scrollProps = {
    ref: scrollRef,
    onScroll: updateFade,
    style: {
      ['--fade-top' as string]: `${fade.top}px`,
      ['--fade-bottom' as string]: `${fade.bottom}px`,
    } as CSSProperties,
  };

  return { scrollProps };
};
